import { useMutation, gql } from '@apollo/client'
import { GET_JWT_FROM_ACCESS_TOKEN_FULFILLED } from '@promoboxx/redux-stores/src/stores/auth/actions'
import jwtDecode from 'jwt-decode'
import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { finishSetup } from '@src/config/I18n'
import loadCrowdinTranslations from '@src/i18n/loadCrowdinTranslations'
import { helpWidget } from '@src/lib/HelpWidget'
import { userSelector } from '@src/stores/app/selectors'

import { jwtRef } from '../../config/ApolloClient'
import { useRefinerSurvey } from '../../lib/useRefinerSurvey'
import { actions, selectors } from '../../stores'
import Loader from '../shared/Loader'

const EXTEND_JWT = gql`
  mutation extendJwt($jwt: String!) {
    extendJwt(jwt: $jwt) {
      jwt
    }
  }
`

const AuthHandler = (props) => {
  const dispatch = useDispatch()
  const [extendJwt] = useMutation(EXTEND_JWT)
  const jwt = useSelector((state) => state.auth.jwt)
  const { authValidationComplete } = useSelector(
    selectors.app.selectAuthValidationComplete,
  )
  const user = useSelector(userSelector)
  const [crowdinLoaded, setCrowdinLoaded] = useState(false)
  const [idleTimeout, setIdleTimeout] = useState(false)
  const { refiner } = useRefinerSurvey()
  // We need to set this here rather than in an effect, as the effect might run
  // _after_ children that are trying to use `jwtRef` have mounted.
  jwtRef.current = jwt

  useEffect(() => {
    if (jwt) {
      try {
        const decoded = jwtDecode(jwt) //This will throw if your JWT is not well formed, etc
        if (decoded) {
          const now = new Date().valueOf() / 1000
          const ttl = (decoded.exp - now - 100) * 1000 // 100s before expiration
          const notExpired = now < decoded.exp
          if (notExpired && !idleTimeout) {
            setIdleTimeout(
              setTimeout(async () => {
                const { data } = await extendJwt({
                  variables: {
                    jwt,
                  },
                })

                if (data && data.extendJwt) {
                  dispatch({
                    type: GET_JWT_FROM_ACCESS_TOKEN_FULFILLED,
                    payload: { response: data.extendJwt },
                  })
                }
                setIdleTimeout(false)
              }, ttl),
            )
          } else if (!notExpired) {
            clearTimeout(idleTimeout)
            dispatch(actions.auth.signOut())
          }
        }
      } catch (error) {
        console.error(error)
      }
    }
  }, [jwt, idleTimeout, dispatch, extendJwt])

  //This effect runs when mounted. It simply validates our auth data
  //regardless of whether or not it fails, we then proceed to load
  //the rest of the app. The assumption is that, auth either set as
  //authenticated or not, and other systems down the chain handle what
  //should happen.
  useEffect(() => {
    dispatch(actions.login.validateAuth())
    //validateAuth()
    //We might add additional async calls here, or only update what we
    //render based on additional state changes. Ideally in the future
    //we have a JWT and can self validate, so no round trip to the
    //server to perform any validation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (authValidationComplete && crowdinLoaded) {
      finishSetup(user.language_preference)
    }
  }, [user, authValidationComplete, crowdinLoaded])

  useEffect(() => {
    if (user && user.id && authValidationComplete) {
      refiner('identifyUser', {
        id: user.id,
        email: user.email,
        name: `${user.first_name} ${user.last_name}`,
      })

      //Zendesk
      helpWidget.identify(user)
    }
  }, [user, authValidationComplete, refiner])

  useEffect(() => {
    loadCrowdinTranslations()
      .then(() => setCrowdinLoaded(true))
      .catch((error) => {
        console.error(error)
        setCrowdinLoaded(true)
      })
  }, [])

  if (authValidationComplete === true && crowdinLoaded) {
    return props.children
  } else {
    return <Loader />
  }
}

export default AuthHandler
